import React, { FC, useEffect, useState, FormEvent, useContext, useCallback } from "react";
import axios from "axios";
import LoaderDiv from "components/common/Loader/index";
import Card from "components/common/Card/index";
import IpinCard from "components/common/Card/ipin/index";
import { CommonContext } from "contexts/CommonContext";
import { NpciCall } from "Utils/npci";
import { useParams } from "react-router-dom";

// Maps cardNetwork value returned by backend to the API path prefix
const CARD_NETWORK_PREFIX: Record<number, string> = {
  1: "rupay",
  2: "mastercard",
  3: "visa",
};

// Derives the network prefix from the current URL path so we can call the
// correct per-network authenticate endpoint without changing the backend URLs.
const getNetworkPrefixFromPath = (): string => {
  const path = window.location.pathname;
  if (path.startsWith("/rupay")) return "rupay";
  if (path.startsWith("/mastercard")) return "mastercard";
  if (path.startsWith("/visa")) return "visa";
  return "";
};

function b64uToBytes(b64u: string): Uint8Array {
  const pad = b64u.length % 4;
  const b64 = (pad ? b64u + "=".repeat(4 - pad) : b64u).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64u(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

function useWindowWidth() {
  const [w, setW] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 375
  );
  React.useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

const SPC_CSS = `
@keyframes spc-pulse{0%,100%{transform:scale(1);opacity:.8}50%{transform:scale(1.04);opacity:1}}
@keyframes spc-dot{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}
@keyframes spc-fadein{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.spc-pulse-circle{animation:spc-pulse 2s ease-in-out infinite}
.spc-dot-1{animation:spc-dot 1.4s infinite ease-in-out;animation-delay:-0.32s}
.spc-dot-2{animation:spc-dot 1.4s infinite ease-in-out;animation-delay:-0.16s}
.spc-dot-3{animation:spc-dot 1.4s infinite ease-in-out}
.spc-dot-4{animation:spc-dot 1.4s infinite ease-in-out;animation-delay:0.16s}
.spc-dot-5{animation:spc-dot 1.4s infinite ease-in-out;animation-delay:0.32s}
.spc-fadein{animation:spc-fadein .5s ease both}
`;

type SpcView = "idle" | "spc" | "success";
const ENCRYPTION_SCRIPTS = [
  "/encryptionJs/bnlib.js",
  "/encryptionJs/forge.min.js",
  "/encryptionJs/hx264.js",
  "/encryptionJs/ppencry.js",
  "/encryptionJs/prng4.js",
  "/encryptionJs/rng.js",
  "/encryptionJs/rsa.js",
];

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load: ${src}`));
    document.head.appendChild(s);
  });

const Home: FC = () => {
  const { token } = useParams();
  const isMobile = useWindowWidth() < 640;
  const [responseData, setResponseData] = useState<any>(null);
  const [notification, setNotification] = useState<string>("");
  const [notificationType, setNotificationType] = useState<"success" | "error">(
    "error",
  );
  const [isResendLimitAvailable, setIsResendLimitAvailable] = useState(true);
  const [enableBiometric, setEnableBiometric] = useState(false);
  const [spcData, setSpcData] = useState<any>(null);
  const [spcView, setSpcView] = useState<SpcView>("idle");
  const [spcNotification, setSpcNotification] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);
  const [spcPaymentResult, setSpcPaymentResult] = useState<{
    amount: string;
    dateTime: string;
  } | null>(null);
  const [spcProcessing, setSpcProcessing] = useState(false);

  const {
    fetchUrl,
    loader,
    setLoader,
    isResendOtpClicked,
    setIsResendOtpClicked,
    cardNetwork,
    setCardNetwork,
    clientImages,
    setClientImages,
    clientName,
    setClientName,
  } = useContext(CommonContext);

  const pathNetworkPrefix = getNetworkPrefixFromPath();
  const networkPrefix = pathNetworkPrefix || CARD_NETWORK_PREFIX[cardNetwork];
  // Capture once: this guard must stay stable for the current mount.
  const [isSpcReturn] = useState<boolean>(() =>
    !!sessionStorage.getItem("spcRegistrationComplete")
  );

  useEffect(() => {
    const spcComplete = sessionStorage.getItem("spcRegistrationComplete");
    if (!spcComplete) return;
    try {
      const returnData = JSON.parse(spcComplete);
      sessionStorage.removeItem("spcRegistrationComplete");
      setTimeout(() => {
        NpciCall(returnData.emvFinalCallbackResponseDTO);
      }, 300);
    } catch {
      sessionStorage.removeItem("spcRegistrationComplete");
    }
  }, []);

  const callSpcCheckStatus = useCallback(
    async (payload: any) => {
      if (!networkPrefix || !fetchUrl) return;
      try {
        const r = await axios.post(
          `${fetchUrl}/${networkPrefix}/api/spc/checkStatus`,
          payload,
          { headers: { "content-type": "application/json" } }
        );
        const d = r.data;
        if (d.sendResponseToEMV || d.sendResponseToNPCI) {
          if (d.spcAuthenticationSuccess) {
            setSpcView("success");
            setTimeout(() => {
              NpciCall(d.emvFinalCallbackResponseDTO ?? d.npciCallbackResponseDTO);
            }, 1500);
          } else {
            NpciCall(d.emvFinalCallbackResponseDTO ?? d.npciCallbackResponseDTO);
          }
        } else {
          setSpcView("idle");
          setSpcData(null);
          setSpcNotification(null);
          setLoader(false);
        }
      } catch {
        setLoader(false);
        window.location.href = networkPrefix ? `/${networkPrefix}/error` : "/error";
      }
    },
    [fetchUrl, networkPrefix, setLoader]
  );

  const startSpcPayment = useCallback(async () => {
    if (!spcData) return;
    if (!("PaymentRequest" in window)) {
      setSpcNotification({
        message: "Biometric authentication not supported. Redirecting to OTP...",
        type: "info",
      });
      setTimeout(() => callSpcCheckStatus({ uuid: token, error: "NotSupportedError" }), 1500);
      return;
    }

    setSpcProcessing(true);
    setSpcNotification(null);
    try {
      const credIds = (spcData.credentialIds || []).map((id: string) =>
        toArrayBuffer(b64uToBytes(id))
      );
      const challengeBuf = toArrayBuffer(b64uToBytes(spcData.challenge));
      const currencyCode = spcData.currency;
      const amount = spcData.value;

      const supportedInstruments: PaymentMethodData[] = [
        {
          supportedMethods: "secure-payment-confirmation",
          data: {
            rpId: spcData.rpId,
            credentialIds: credIds,
            challenge: challengeBuf,
            instrument: {
              displayName: spcData.displayName,
              icon: spcData.icon,
              iconMustBeShown: false,
            },
            payeeOrigin: spcData.payeeOrigin || window.location.origin,
            timeout: spcData.timeout,
          },
        },
      ];

      const details: PaymentDetailsInit = {
        total: { label: "Total", amount: { currency: currencyCode, value: amount } },
      };

      const request = new PaymentRequest(supportedInstruments, details);
      const resp = await request.show();
      const cred = resp.details as PublicKeyCredential;

      const c = (cred as any).toJSON
        ? (cred as any).toJSON()
        : {
            id: cred.id,
            type: cred.type,
            response: {
              clientDataJSON: bytesToB64u((cred.response as any).clientDataJSON),
              authenticatorData: bytesToB64u((cred.response as any).authenticatorData),
              signature: bytesToB64u((cred.response as any).signature),
              userHandle: (cred.response as any).userHandle
                ? bytesToB64u((cred.response as any).userHandle)
                : null,
            },
          };

      const payload = {
        uuid: token,
        spcAuthenticationData: { id: c.id, type: c.type, response: c.response },
      };

      const displayAmount = [currencyCode, amount].filter(Boolean).join(" ") || amount || "0";
      setSpcPaymentResult({
        amount: displayAmount,
        dateTime: new Date().toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      });

      await resp.complete("success");
      await callSpcCheckStatus(payload);
    } catch (err: any) {
      const errName: string = err?.name || "UnknownError";
      const msgMap: Record<string, string> = {
        NotAllowedError: "Authentication cancelled. Redirecting to OTP...",
        SecurityError: "Security error occurred. Redirecting to OTP...",
        NotSupportedError: "Biometric not available. Redirecting to OTP...",
        AbortError: "Authentication aborted. Redirecting to OTP...",
      };
      setSpcNotification({
        message: msgMap[errName] ?? "Biometric authentication failed. Redirecting to OTP...",
        type: "info",
      });
      await callSpcCheckStatus({ uuid: token, error: errName });
    } finally {
      setSpcProcessing(false);
    }
  }, [spcData, token, callSpcCheckStatus]);

  const handleUseOtpInstead = useCallback(() => {
    callSpcCheckStatus({ uuid: token, error: "UserRequestedOTP" });
  }, [token, callSpcCheckStatus]);

  const handleEncrypt = (
    modulus: string,
    exponent: string,
    dataToEncrypt: string,
  ) => {
    let encryptedData =
      typeof window !== "undefined" &&
      (window as any)["processIPin"](modulus, exponent, dataToEncrypt);
    return encryptedData;
  };

  // OOB polling handler
  const handleOobSubmit = () => {
    if (!networkPrefix) return;
    setLoader(true);
    axios
      .post(
        `${fetchUrl}/${networkPrefix}/api/oob/checkStatus`,
        { uuid: token },
        { headers: { "content-type": "application/json" } },
      )
      .then((response) => {
        const respData = response.data;
        if (respData.sendResponseToEMV || respData.sendResponseToNPCI) {
          NpciCall(
            respData.emvFinalCallbackResponseDTO ??
              respData.npciCallbackResponseDTO,
          );
        }
      })
      .catch((error) => {
        console.error("OOB polling error:", error);
      })
      .finally(() => setLoader(false));
  };

  // OOB polling & session timeout lifecycle
  useEffect(() => {
    let oobTimeoutId: ReturnType<typeof setTimeout>;
    let oobIntervalId: ReturnType<typeof setInterval>;
    let sessionTimeoutId: ReturnType<typeof setTimeout>;

    if (responseData) {
      if (responseData.challengeType === 6) {
        const oobInitialWait =
          responseData.oobInitialPollingWaitTimeInSecond || 10;
        const oobInterval = responseData.oobPollingIntervalInSecond || 15;
        oobTimeoutId = setTimeout(() => {
          handleOobSubmit();
          oobIntervalId = setInterval(handleOobSubmit, oobInterval * 1000);
        }, oobInitialWait * 1000);
      }
      if (responseData.timeout) {
        sessionTimeoutId = setTimeout(
          () => handleCancelClick(),
          responseData.timeout * 1000,
        );
      }
    }

    return () => {
      if (oobTimeoutId) clearTimeout(oobTimeoutId);
      if (oobIntervalId) clearInterval(oobIntervalId);
      if (sessionTimeoutId) clearTimeout(sessionTimeoutId);
    };
  }, [responseData]);

  // Lazily load encryption scripts only for IPIN (challengeType 8)
  useEffect(() => {
    if (responseData?.challengeType === 8) {
      ENCRYPTION_SCRIPTS.reduce(
        (chain, src) => chain.then(() => loadScript(src)),
        Promise.resolve(),
      );
    }
  }, [responseData?.challengeType]);

  // Initial authenticate call — hits the per-network endpoint derived from the
  // current URL path. Response includes clientImages and cardNetwork so all
  // subsequent calls can use the right prefix without any hardcoding.
  useEffect(() => {
    if (!fetchUrl) return;
    if (isSpcReturn) return;
    const urlNetwork = getNetworkPrefixFromPath();
    if (!urlNetwork) {
      window.location.href = "/error";
      return;
    }
    setLoader(true);
    axios
      .get(`${fetchUrl}/${urlNetwork}/api/authenticate/${token}`)
      .then((response) => {
        const resData = response.data;
        if (resData.sendResponseToEMV || resData.sendResponseToNPCI) {
          NpciCall(
            resData.emvFinalCallbackResponseDTO ??
              resData.npciCallbackResponseDTO,
          );
        } else if (resData.responseCode === 0) {
          // cardNetwork and clientName can be null at root; fall back to clientDetailId / emvTransactionData
          setCardNetwork(resData.cardNetwork ?? resData.clientDetailId);
          setClientName(resData.clientName ?? "");
          setClientImages(
            resData.emvTransactionData?.clientImageDataDTO ?? null,
          );
          setResponseData(resData.emvTransactionData);
          if (resData.spcTransactionData) {
            setSpcData(resData.spcTransactionData);
            setSpcView("spc");
          }
        } else {
          window.location.href = `/${urlNetwork}/error`;
        }
      })
      .catch((error) => {
        console.error("Error fetching authenticate data:", error);
        window.location.href = `/${urlNetwork}/error`;
      })
      .finally(() => setLoader(false));
  }, [fetchUrl, isSpcReturn, token, setLoader, setCardNetwork, setClientName, setClientImages]);

  // Resend OTP
  const handleResendOtpClick = () => {
    if (!networkPrefix) return;
    axios
      .post(
        `${fetchUrl}/${networkPrefix}/api/resendOTP`,
        { uuid: token },
        { headers: { "content-type": "application/json" } },
      )
      .then((response) => {
        const respData = response.data;
        const isResendLimitExhausted =
          respData.leftResendOTPCount != null &&
          respData.leftResendOTPCount === 0;
        const leftAttemptsMessage =
          respData.leftResendOTPCount != null && respData.leftResendOTPCount > 0
            ? ` You have ${respData.leftResendOTPCount} resend attempt${respData.leftResendOTPCount === 1 ? "" : "s"} remaining.`
            : "";
        if (respData.responseCode === 0) {
          setNotificationType("success");
          setNotification(`OTP resent successfully.${leftAttemptsMessage}`);
          if (isResendLimitExhausted) setIsResendLimitAvailable(false);
        } else {
          setNotificationType("error");
          setNotification(respData.responseMessage);
        }
        if (!respData.sendResponseToEMV) {
          setIsResendOtpClicked(true);
        } else {
          setIsResendLimitAvailable(false);
          NpciCall(
            respData.emvFinalCallbackResponseDTO ??
              respData.npciCallbackResponseDTO,
          );
        }
      })
      .catch(() => setIsResendOtpClicked(true));
  };

  // Cancel transaction
  const handleCancelClick = () => {
    if (!networkPrefix) return;
    setLoader(true);
    axios
      .post(
        `${fetchUrl}/${networkPrefix}/api/cancel`,
        { uuid: token },
        { headers: { "content-type": "application/json" } },
      )
      .then((response) => {
        const respData = response.data;
        if (respData.sendResponseToEMV || respData.sendResponseToNPCI) {
          NpciCall(
            respData.emvFinalCallbackResponseDTO ??
              respData.npciCallbackResponseDTO,
          );
        } else {
          window.location.href = "/error";
        }
      })
      .catch(() => setLoader(false));
  };

  // Verify OTP/IPIN
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!networkPrefix) return;
    const challengeType = responseData?.challengeType;
    let otpValue = (document.getElementById(challengeType === 8 ? "ipin" : "otp") as HTMLInputElement)?.value;
    const otpValueLength =
      challengeType === 8 ? responseData?.challengeLength : 6;
    if (otpValue.length < otpValueLength) {
      if (challengeType === 8) {
        setNotificationType("error");
        setNotification(`Please enter a ${otpValueLength}-digit IPIN.`);
      } else {
        setNotificationType("error");
        setNotification(`Please enter a ${otpValueLength}-digit OTP.`);
      }
      return;
    }
    const dataToSend: Record<string, any> = {
      uuid: token,
      emvSecureOtp: otpValue,
    };
    if (enableBiometric && responseData?.spcSupported) {
      dataToSend.spcRegistrationRequested = true;
    }
    if (challengeType === 8) {
      const modulus = responseData?.modulus;
      const exponent = responseData?.exponent;
      if (!modulus || !exponent) {
        setNotificationType("error");
        setNotification("Unable to process IPIN challenge.");
        return;
      }
       const encryptedOtp = handleEncrypt(modulus, exponent, otpValue);
       if (!encryptedOtp) {
         setNotificationType("error");
         setNotification("Unable to encrypt IPIN.");
         return;
       }

       dataToSend.emvSecureOtp = encryptedOtp;
    }
    axios
      .post(
        `${fetchUrl}/${networkPrefix}/api/verifyOTP`,
         dataToSend,
        { headers: { "content-type": "application/json" } },
      )
      .then((response) => {
        const respData = response.data;
        if (respData.sendResponseToEMV) {
          setNotificationType(respData.responseCode === 0 ? "success" : "error");
          if (
            respData.responseCode === 0 &&
            respData.spcRegistrationRequested &&
            respData.cardNumber
          ) {
            sessionStorage.setItem(
              "sendResponseToEMVData",
              JSON.stringify({
                emvFinalCallbackResponseDTO: respData.emvFinalCallbackResponseDTO,
                returnUrl: window.location.href,
              })
            );
            sessionStorage.setItem("spcCardNumber", respData.cardNumber);
            window.location.href = "/spc/register";
            return;
          }

          setNotificationType(
            respData.responseCode === 0 ? "success" : "error",
          );
          const cardBlockMsg = (respData.cardBlockMessage || "").trim();
          setNotification(
            `${respData.responseMessage}${cardBlockMsg ? ` ${cardBlockMsg}` : ""}`,
          );
          if (cardBlockMsg) {
            setTimeout(
              () => NpciCall(respData.emvFinalCallbackResponseDTO),
              3000,
            );
          } else {
            NpciCall(respData.emvFinalCallbackResponseDTO);
          }
        } else {
          const leftAttemptsMessage =
            respData.leftRetryOTPCount != null
              ? ` You have ${respData.leftRetryOTPCount} attempt${respData.leftRetryOTPCount === 1 ? "" : "s"} remaining.`
              : "";
          setNotificationType(
            respData.responseCode === 0 ? "success" : "error",
          );
          setNotification(`${respData.responseMessage}${leftAttemptsMessage}`);
        }
      })
      .catch(console.error);
  };

  const handleKeyDownNotification = (message: string) =>
    setNotification(message);

  if (spcView === "spc" && spcData) {
    const amt = [spcData.currency, spcData.value].filter(Boolean).join(" ") || spcData.value || "0";
    return (
      <>
        <style>{SPC_CSS}</style>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            fontFamily: "'Roboto','Segoe UI',sans-serif",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: isMobile ? "14px 20px" : "18px 40px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
              onClick={handleUseOtpInstead}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#064e3b",
                  fontFamily: "'Titillium Web',sans-serif",
                }}
              >
                pine labs
              </span>
            </div>
            {spcData.psImageSpc?.defaultImage ? (
              <img src={spcData.psImageSpc.defaultImage} alt="network" style={{ height: "28px", objectFit: "contain" }} />
            ) : spcData.displayName ? (
              <div
                style={{
                  padding: "4px 10px",
                  borderRadius: "6px",
                  background: "#1a56db",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "13px",
                }}
              >
                {spcData.displayName}
              </div>
            ) : null}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "32px 24px" : "40px 60px",
              maxWidth: "520px",
              margin: "0 auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {spcNotification && (
              <div
                className="spc-fadein"
                style={{
                  width: "100%",
                  marginBottom: "20px",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  backgroundColor:
                    spcNotification.type === "info"
                      ? "#eff6ff"
                      : spcNotification.type === "success"
                        ? "#f0fdf4"
                        : "#fef2f2",
                  border: `1px solid ${
                    spcNotification.type === "info"
                      ? "#93c5fd"
                      : spcNotification.type === "success"
                        ? "#bbf7d0"
                        : "#fecaca"
                  }`,
                  color:
                    spcNotification.type === "info"
                      ? "#1d4ed8"
                      : spcNotification.type === "success"
                        ? "#16a34a"
                        : "#991b1b",
                }}
              >
                {spcNotification.message}
              </div>
            )}

            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <div
                style={{
                  fontSize: isMobile ? "28px" : "32px",
                  fontWeight: 700,
                  color: "#064e3b",
                  fontFamily: "'Titillium Web',sans-serif",
                  lineHeight: "1.2",
                }}
              >
                Paying {amt}
              </div>
              {(spcData.payeeName || responseData?.merchantName) && (
                <div
                  style={{
                    fontSize: "16px",
                    color: "#6b7280",
                    marginTop: "6px",
                    fontFamily: "'Roboto',sans-serif",
                  }}
                >
                  to {spcData.payeeName || responseData?.merchantName}
                </div>
              )}
            </div>

            <div className={spcProcessing ? "" : "spc-pulse-circle"} style={{ position: "relative", marginBottom: "28px" }}>
              <div
                style={{
                  width: isMobile ? "140px" : "160px",
                  height: isMobile ? "140px" : "160px",
                  borderRadius: "50%",
                  border: "3px solid #064e3b",
                  backgroundColor: "#f0fdf4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width={isMobile ? "72" : "84"} height={isMobile ? "72" : "84"} viewBox="0 0 80 80" fill="none" stroke="#064e3b" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M40 8a32 32 0 1 0 0 64 32 32 0 0 0 0-64z" strokeOpacity=".15" fill="#064e3b" fillOpacity=".07" />
                  <path d="M40 20c-5 0-9 2-12 5" />
                  <path d="M28 32c0-6.6 5.4-12 12-12s12 5.4 12 12" />
                  <path d="M28 40c0-6.6 5.4-12 12-12" />
                  <path d="M40 28c3.3 0 6 2.7 6 6v10" />
                  <path d="M34 34c0-3.3 2.7-6 6-6" />
                  <path d="M40 40v12" />
                  <path d="M34 46c2 4 6 6 6 6" />
                  <path d="M52 32v8c0 8-5 14-12 16" />
                  <path d="M28 44c1 5 4 9 8 12" />
                </svg>
              </div>
              {spcProcessing && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "-14px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "5px",
                  }}
                >
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`spc-dot-${i}`}
                      style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#064e3b" }}
                    />
                  ))}
                </div>
              )}
            </div>

            <h2 style={{ fontSize: isMobile ? "20px" : "22px", fontWeight: 700, color: "#111827", margin: "0 0 12px", textAlign: "center", fontFamily: "'Titillium Web',sans-serif" }}>
              Approve Payment Using Biometrics
            </h2>
            {spcData.challengeInfoText && (
              <p style={{ fontSize: "13px", color: "#6b7280", textAlign: "center", margin: "0 0 24px", lineHeight: "1.6", maxWidth: "340px" }}>
                {spcData.challengeInfoText}
              </p>
            )}

            <button
              onClick={startSpcPayment}
              disabled={spcProcessing}
              style={{
                width: "100%",
                maxWidth: "360px",
                padding: "15px",
                fontSize: "16px",
                fontWeight: 700,
                color: "#fff",
                backgroundColor: spcProcessing ? "#94a3b8" : "#064e3b",
                border: "none",
                borderRadius: "12px",
                cursor: spcProcessing ? "not-allowed" : "pointer",
                fontFamily: "'Titillium Web',sans-serif",
                marginBottom: "16px",
              }}
            >
              {spcProcessing ? "Authenticating..." : "Authenticate"}
            </button>
            <button
              onClick={handleUseOtpInstead}
              disabled={spcProcessing}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#064e3b",
                fontSize: "14px",
                fontWeight: 600,
                textDecoration: "underline",
                padding: "4px",
              }}
            >
              Use OTP Instead
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: isMobile ? "14px 20px" : "18px 40px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <div>
              <div style={{ fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
                POWERED BY
              </div>
              <div style={{ fontSize: "14px", fontWeight: 700, color: "#064e3b", fontFamily: "'Titillium Web',sans-serif" }}>
                pine labs
              </div>
            </div>
            <div
              style={{
                padding: "4px 8px",
                borderRadius: "6px",
                border: "2px solid #1a56db",
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              <span style={{ fontSize: "11px", fontWeight: 800, color: "#1a56db" }}>PCI</span>
              <span style={{ fontSize: "9px", fontWeight: 700, color: "#1a56db", lineHeight: "1" }}>DSS</span>
            </div>
          </div>
        </div>
        <form id="paymentPostForm" name="postForm" className="hide" method="POST" onSubmit={(e) => e.preventDefault()}>
          <input type="hidden" id="cres" name="cres" />
          <input type="hidden" id="threeDSSessionData" name="threeDSSessionData" />
        </form>
        <LoaderDiv loader={loader} />
      </>
    );
  }

  if (spcView === "success" && spcPaymentResult) {
    return (
      <>
        <style>{SPC_CSS}</style>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: isMobile ? "flex-start" : "center",
            background: "#fff",
            fontFamily: "'Roboto','Segoe UI',sans-serif",
            padding: isMobile ? "40px 24px 32px" : "40px 20px",
            boxSizing: "border-box",
          }}
        >
          <div className="spc-fadein" style={{ width: "100%", maxWidth: "420px" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(22,163,74,.3)",
                }}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <h2
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: "#111827",
                textAlign: "center",
                margin: "0 0 6px",
                fontFamily: "'Titillium Web',sans-serif",
              }}
            >
              Authentication Successful
            </h2>
            <p style={{ textAlign: "center", fontSize: "13px", color: "#6b7280", margin: "0 0 24px" }}>
              Transaction Details
            </p>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                overflow: "hidden",
                marginBottom: "32px",
                boxShadow: "0 1px 4px rgba(0,0,0,.05)",
              }}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>Amount</span>
                <span style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                  {spcPaymentResult.amount}
                </span>
              </div>
              <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>Date and Time</span>
                <span style={{ fontSize: "13px", color: "#374151" }}>{spcPaymentResult.dateTime}</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "12px" }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`spc-dot-${i}`}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: i <= 2 ? "#93c5fd" : i === 3 ? "#064e3b" : "#374151",
                  }}
                />
              ))}
            </div>
            <p style={{ textAlign: "center", color: "#6b7280", fontSize: "13px", margin: 0 }}>
              Redirecting you to the merchant...
            </p>
          </div>
        </div>
        <form id="paymentPostForm" name="postForm" className="hide" method="POST" onSubmit={(e) => e.preventDefault()}>
          <input type="hidden" id="cres" name="cres" />
          <input type="hidden" id="threeDSSessionData" name="threeDSSessionData" />
        </form>
        <LoaderDiv loader={loader} />
      </>
    );
  }

  return (
    <>
      {!loader && responseData && cardNetwork > 0 && (
        responseData.challengeType === 8 ? (
          <IpinCard
            updateNotification={handleKeyDownNotification}
            notification={notification}
            notificationType={notificationType}
            responseData={responseData}
            clientImages={clientImages}
            clientName={clientName}
            isResendIpinClicked={isResendOtpClicked}
            handleResendIpinClick={handleResendOtpClick}
            handleSubmit={(e: any) => handleSubmit(e)}
            handleCancelClick={handleCancelClick}
            isResendLimitAvailable={isResendLimitAvailable}
            handleOobSubmit={handleOobSubmit}
          />
        ) : (
          <Card
            updateNotification={handleKeyDownNotification}
            notification={notification}
            notificationType={notificationType}
            responseData={responseData}
            clientImages={clientImages}
            clientName={clientName}
            isResendOtpClicked={isResendOtpClicked}
            handleResendOtpClick={handleResendOtpClick}
            handleSubmit={(e: any) => handleSubmit(e)}
            handleCancelClick={handleCancelClick}
            isResendLimitAvailable={isResendLimitAvailable}
            handleOobSubmit={handleOobSubmit}
            enableBiometric={enableBiometric}
            setEnableBiometric={setEnableBiometric}
          />
        )
      )}
      {/* Single shared hidden form used by NpciCall for all card networks */}
      <form
        id="paymentPostForm"
        name="postForm"
        className="hide"
        method="POST"
        onSubmit={(e) => e.preventDefault()}
      >
        <input type="hidden" id="cres" name="cres" />
        <input
          type="hidden"
          id="threeDSSessionData"
          name="threeDSSessionData"
        />
      </form>
      <LoaderDiv loader={loader} />
    </>
  );
};

export default Home;
